/* © CNES */
import * as gjv from 'geojson-validation';
import shp from 'shpjs/dist/shp.min.js';
import { GeoJsonPolygon } from '../../../../../../models/GeoJsonPolygon';
/**
 * function that parse GeoJson file
 * @param file file to parse
 * @returns promise
 */
export const readGeoJsonFile = (file: File) => {
  return new Promise((resolve, reject) => {
    if (file.name.split('.').pop() !== 'geojson') {
      reject(
        new Error('Extension not allowed, please choose a .GeoJSON file.')
      );
    } else {
      const fileReader = new FileReader();
      fileReader.onload = (event) => {
        if (event.target) {
          try {
            const parsedData = JSON.parse(event.target.result as string);
            if (gjv.valid(parsedData)) {
              if (parsedData.features.length === 0) {
                reject(new Error('No geographical entity found.'));
              } else if (parsedData.features.length > 1) {
                reject(new Error('Several geographical areas defined.'));
              } else {
                resolve(parsedData);
              }
            } else reject(new Error('Not valid geoJson file'));
          } catch (error) {
            reject(error);
          }
        }
      };

      fileReader.onerror = (error) => reject(error);
      fileReader.readAsText(file);
    }
  });
};

/**
 * function that parse zip->SHP file
 * @param file file to parse
 * @returns
 */
export const readShapeFile = (file: File) => {
  return new Promise((resolve, reject) => {
    const fileReader = new FileReader();
    let fileType = file.name.split('.').pop();
    if (file.name.split('.').pop() !== 'zip') {
      reject(new Error('Extension not allowed, please choose a .ZIP file.'));
    } else {
      fileReader.onload = (e) => {
        if (e.target?.result) {
          if (fileType === 'zip') {
            shp(e.target.result)
              .then((geojson: GeoJsonPolygon) => {
                resolve(geojson);
              })
              .catch((error: Error) => {
                reject(error);
              });
          }
        }
      };
      fileReader.onerror = (error) => reject(error);
      fileReader.readAsArrayBuffer(file);
    }
  });
};

/**
 * Read KML file.
 * @param file loaded file.
 * @returns Promise<GeoJsonPolygon>
 */
export const readKMLFile = (file: File) => {
  return new Promise<GeoJsonPolygon>((resolve, reject) => {
    const fileReader = new FileReader();
    if (file.name.split('.').pop() !== 'kml') {
      reject(new Error('Extension not allowed, please choose a .KML file.'));
    }
    fileReader.onload = async (e: any) => {
      let result = extractXmlCoords(e.target.result, reject);
      const coords = result?.split(',');
      const coordsAltitudeFiltered = coords
        ?.map((coord) => {
          return coord.split(' ');
        })
        .flat();

      const tuppleCoords = coordsAltitudeFiltered?.reduce(
        (acc, currentValue, currentIndex) => {
          if (currentIndex % 3 === 0) {
            acc.push([
              +currentValue,
              +coordsAltitudeFiltered[currentIndex + 1],
              +coordsAltitudeFiltered[currentIndex + 2],
            ]);
          }
          return acc;
        },
        [] as number[][]
      );

      const geoJson = new GeoJsonPolygon();

      if (tuppleCoords) {
        geoJson.features[0].geometry.coordinates = [tuppleCoords];
        resolve(geoJson);
      }
    };

    fileReader.onerror = (error) => reject(error);
    fileReader.readAsText(file);
  });
};

/**
 *
 * @param plainText The plainText from File loaded.
 * @returns a string of coordinates from Polygon tag.
 */
const extractXmlCoords = (plainText: string, reject: Function) => {
  let parser = new DOMParser();
  let xmlDoc = parser.parseFromString(plainText, 'text/xml');
  let coords: string = '';

  if (xmlDoc.documentElement.nodeName === 'kml') {
    for (const item of xmlDoc.getElementsByTagName('Placemark') as any) {
      let polygons = item.getElementsByTagName('Polygon');
      if (!polygons.length) {
        reject(new Error('No polygon tag was found in KML File.'));
      }
      /** POLYGONS PARSE **/
      for (const polygon of polygons) {
        coords = polygon
          .getElementsByTagName('coordinates')[0]
          .childNodes[0].nodeValue.trim();
      }
    }
    return coords;
  } else {
    reject(new Error('error while parsing KML File.'));
  }
};
